import React, { useState } from "react";
import { useSelector } from "react-redux";

import DialPhoneField from "./DialPhoneField";
import { CommonToast } from "../../../common/Toast";
import { useCreateCallbackRequestMutation } from "../../../../store/Callback/callbackApiSlice";
import { OTHER_OPTION } from "../../../../constants/tradeVerticals";

/**
 * Callback ("talk to our team") form shown at the bottom of every Trade vertical
 * page. On submit it persists a CallbackRequest and emails support@trevex.io.
 *
 * Props:
 *  - product:  "information" | "finance" | "risk"
 *  - options:  string[] for the "How can we help?" dropdown (includes "Other")
 *  - isMember: boolean — best-effort member flag, included in the request
 */
const CallbackForm = ({ product, options = [], isMember = false }) => {
  const { token } = useSelector((state) => state.auth);
  const [createCallbackRequest, { isLoading }] = useCreateCallbackRequestMutation();

  const [contactName, setContactName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [dial, setDial] = useState("+971");
  const [phone, setPhone] = useState("");
  const [need, setNeed] = useState("");
  const [note, setNote] = useState("");
  const [needOpen, setNeedOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const isOther = need === OTHER_OPTION;

  const reset = () => {
    setContactName("");
    setBusinessName("");
    setDial("+971");
    setPhone("");
    setNeed("");
    setNote("");
    setErrors({});
  };

  const validate = () => {
    const next = {};
    if (!contactName.trim()) next.contactName = "Please enter your name.";
    if (!businessName.trim()) next.businessName = "Please enter your business name.";
    if (!phone) next.phone = "Please enter a number we can call.";
    if (!need) next.need = "Please pick an option.";
    if (need === OTHER_OPTION && !note.trim()) next.note = "Please tell us a little more.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    if (!validate()) return;

    try {
      await createCallbackRequest({
        data: {
          contact_name: contactName.trim(),
          business_name: businessName.trim(),
          phone_number: `${dial} ${phone}`.trim(),
          product,
          need,
          note: isOther ? note.trim() : "",
          is_member: !!isMember,
        },
        token,
      }).unwrap();
      CommonToast.success("Thanks — a member of our team will call you back, usually the same working day.");
      reset();
    } catch (err) {
      CommonToast.error("Sorry, something went wrong sending your request. Please try again.");
    }
  };

  const selectNeed = (option) => {
    setNeed(option);
    setNeedOpen(false);
    setErrors((prev) => ({ ...prev, need: undefined }));
  };

  const labelStyle = { fontSize: "11.5px", fontWeight: 600, color: "#5A6B73" };
  const fieldStyle = {
    width: "100%",
    border: "1px solid #DCE3E8",
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "13.5px",
    fontFamily: "inherit",
    color: "#002940",
    outline: "none",
    boxSizing: "border-box",
  };
  const checkIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FB512D" strokeWidth="2.4">
      <path d="M5 12.5 10 17.5 19 7" />
    </svg>
  );

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: "#fff",
        border: "1px solid #E4EAEE",
        borderRadius: "12px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* Your name */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <label style={labelStyle}>Your name</label>
        <input
          placeholder="e.g. Marcus Sterling"
          value={contactName}
          onChange={(e) => {
            setContactName(e.target.value);
            setErrors((prev) => ({ ...prev, contactName: undefined }));
          }}
          style={{ ...fieldStyle, borderColor: errors.contactName ? "#E11D48" : "#DCE3E8" }}
        />
        {errors.contactName && (
          <span style={{ fontSize: "12px", color: "#E11D48" }}>{errors.contactName}</span>
        )}
      </div>

      {/* Business name */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <label style={labelStyle}>Business name</label>
        <input
          placeholder="Your company"
          value={businessName}
          onChange={(e) => {
            setBusinessName(e.target.value);
            setErrors((prev) => ({ ...prev, businessName: undefined }));
          }}
          style={{ ...fieldStyle, borderColor: errors.businessName ? "#E11D48" : "#DCE3E8" }}
        />
        {errors.businessName && (
          <span style={{ fontSize: "12px", color: "#E11D48" }}>{errors.businessName}</span>
        )}
      </div>

      {/* Phone — country-code + mobile, matching the agent-intake Nominee field */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <DialPhoneField
          dial={dial}
          onDialChange={setDial}
          phone={phone}
          onPhoneChange={(v) => {
            setPhone(v);
            setErrors((prev) => ({ ...prev, phone: undefined }));
          }}
          error={!!errors.phone}
        />
        {errors.phone && (
          <span style={{ fontSize: "12px", color: "#E11D48" }}>{errors.phone}</span>
        )}
      </div>

      {/* How can we help? — custom dropdown */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <label style={labelStyle}>How can we help?</label>
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setNeedOpen((o) => !o)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              border: `1px solid ${errors.need ? "#E11D48" : "#DCE3E8"}`,
              borderRadius: "8px",
              padding: "10px 12px",
              background: "#fff",
              fontSize: "13.5px",
              fontFamily: "inherit",
              cursor: "pointer",
              boxSizing: "border-box",
            }}
          >
            <span
              style={{
                color: need ? "#002940" : "#9AA8AF",
                fontWeight: need ? 500 : 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {need || "Select an option"}
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9AA8AF" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {needOpen && (
            <>
              {/* click-away layer */}
              <div
                onClick={() => setNeedOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 35 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "46px",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #E4EAEE",
                  borderRadius: "11px",
                  boxShadow: "0 14px 36px rgba(0,30,48,.16)",
                  padding: "6px",
                  zIndex: 40,
                }}
              >
                {options.map((option) => (
                  <button
                    type="button"
                    key={option}
                    onClick={() => selectNeed(option)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "9px 10px",
                      border: "none",
                      background: "transparent",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontFamily: "inherit",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F3F6F8")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <span style={{ flex: 1, textAlign: "left", color: "#002940" }}>{option}</span>
                    {need === option && checkIcon}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {errors.need && (
          <span style={{ fontSize: "12px", color: "#E11D48" }}>{errors.need}</span>
        )}
      </div>

      {/* Free-text note — only when "Other" is chosen */}
      {isOther && (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={labelStyle}>Tell us more</label>
          <textarea
            placeholder="A sentence or two about what you need."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            style={{ ...fieldStyle, resize: "vertical", borderColor: errors.note ? "#E11D48" : "#DCE3E8" }}
          />
          {errors.note && (
            <span style={{ fontSize: "12px", color: "#E11D48" }}>{errors.note}</span>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        style={{
          width: "100%",
          background: "#FB512D",
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          padding: "13px",
          fontSize: "14.5px",
          fontWeight: 700,
          fontFamily: "inherit",
          cursor: isLoading ? "not-allowed" : "pointer",
          opacity: isLoading ? 0.7 : 1,
          marginTop: "2px",
        }}
      >
        {isLoading ? "Sending…" : "Talk to our team"}
      </button>
    </form>
  );
};

export default CallbackForm;
